import qrcode from 'qrcode-generator';

/**
 * 繪製 QR Code 到 Canvas
 * 優化點：確保安靜區、限制定位點樣式、優化 Logo 挖空計算
 */
export const drawQRCode = (canvas, content, options) => {
  if (!canvas || !content) return;

  const {
    typeNumber = 0,
    errorCorrectionLevel = 'H', 
    fgColor = '#000000',
    bgColor = '#FFFFFF',
    dotStyle = 'rounded',
    cornerStyle = 'rounded',
    logo = null
  } = options;

  try {
    const qr = qrcode(typeNumber, errorCorrectionLevel);
    // 處理內容：確保 URL 有協議頭
    let processedContent = content;
    if (content.startsWith('www.')) processedContent = 'https://' + content;
    
    qr.addData(processedContent);
    qr.make();

    const moduleCount = qr.getModuleCount();
    const ctx = canvas.getContext('2d');
    
    // 高解析度設定
    const size = 1000; 
    const cellSize = size / moduleCount;
    // 重要：安靜區 (Quiet Zone) 必須至少為 4 個單元
    const margin = cellSize * 4; 
    const totalSize = size + margin * 2;

    canvas.width = totalSize;
    canvas.height = totalSize;

    // 1. 填充背景
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, totalSize, totalSize);

    // 2. 繪製數據點
    ctx.fillStyle = fgColor;

    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        const isDark = qr.isDark(row, col);
        if (!isDark) continue;

        // 定位點判斷 (Finder Patterns: 7x7 squares in corners)
        const isLeftTop = row < 7 && col < 7;
        const isRightTop = row < 7 && col >= moduleCount - 7;
        const isLeftBottom = row >= moduleCount - 7 && col < 7;
        const isCorner = isLeftTop || isRightTop || isLeftBottom;

        // 中心 Logo 挖空邏輯 (只在有 Logo 時生效)
        // 限制挖空範圍在 20% 以內，確保 H 級容錯能補回
        const centerStart = Math.floor(moduleCount * 0.4);
        const centerEnd = Math.floor(moduleCount * 0.6);
        const isCenterArea = logo && row >= centerStart && row < centerEnd && col >= centerStart && col < centerEnd;

        if (isCenterArea) continue;

        const x = margin + col * cellSize;
        const y = margin + row * cellSize;

        // 核心修正：定位點不能使用 'dots'，否則 90% 的手機掃不到
        // 強制定位點至少使用 'rounded' 或 'square'
        let finalStyle = dotStyle;
        if (isCorner) {
          finalStyle = cornerStyle === 'dots' ? 'rounded' : cornerStyle;
        }

        drawCell(ctx, x, y, cellSize, finalStyle);
      }
    }

    // 3. 繪製 Logo
    if (logo) {
      drawLogo(ctx, logo, totalSize, bgColor);
    }

  } catch (error) {
    console.error('QR Generation Error:', error);
  }
};

const drawCell = (ctx, x, y, size, style) => {
  const padding = size * 0.05; // 點與點之間的微小間隙
  const drawSize = size - padding;
  
  ctx.beginPath();
  switch (style) {
    case 'dots':
      ctx.arc(x + size / 2, y + size / 2, (size / 2) * 0.8, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'rounded':
      const r = size * 0.25;
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + size - r, y);
      ctx.quadraticCurveTo(x + size, y, x + size, y + r);
      ctx.lineTo(x + size, y + size - r);
      ctx.quadraticCurveTo(x + size, y + size, x + size - r, y + size);
      ctx.lineTo(x + r, y + size);
      ctx.quadraticCurveTo(x, y + size, x, y + size - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.fill();
      break;
    case 'square':
    default:
      ctx.fillRect(x, y, size + 0.5, size + 0.5); // 稍微溢出避免間隙
      break;
  }
};

const drawLogo = (ctx, logoSrc, canvasSize, bgColor) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = logoSrc;
  img.onload = () => {
    // Logo 尺寸建議不超過總面積的 15%
    const logoAreaSize = canvasSize * 0.2; 
    const x = (canvasSize - logoAreaSize) / 2;
    const y = (canvasSize - logoAreaSize) / 2;
    
    // 繪製白色底座（稍微大一點點）
    ctx.fillStyle = bgColor;
    const padding = canvasSize * 0.015;
    
    // 繪製圓角矩形底座
    const r = padding * 2;
    const bx = x - padding;
    const by = y - padding;
    const bw = logoAreaSize + padding * 2;
    const bh = logoAreaSize + padding * 2;
    
    ctx.beginPath();
    ctx.moveTo(bx + r, by);
    ctx.lineTo(bx + bw - r, by);
    ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
    ctx.lineTo(bx + bw, by + bh - r);
    ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
    ctx.lineTo(bx + r, by + bh);
    ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
    ctx.lineTo(bx, by + r);
    ctx.quadraticCurveTo(bx, by, bx + r, by);
    ctx.fill();

    // 繪製 Logo
    const ratio = img.width / img.height;
    let w = logoAreaSize;
    let h = logoAreaSize;
    if (ratio > 1) {
      h = w / ratio;
    } else {
      w = h * ratio;
    }
    ctx.drawImage(img, (canvasSize - w) / 2, (canvasSize - h) / 2, w, h);
  };
};

export const downloadCanvas = (canvas, filename) => {
  if (!canvas) return;
  // 確保 Logo 已經繪製完成。因為 drawLogo 是非同步的，
  // 這裡簡單的做法是直接獲取當前 canvas 內容。
  // 在生產環境中，建議使用 Promise 管理 Logo 加載。
  const dataUrl = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
};

export const getRandomColor = () => {
  const colors = [
    '#6366f1', '#4f46e5', '#f43f5e', '#e11d48', '#0891b2', 
    '#059669', '#7c3aed', '#2563eb', '#db2777'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};